import { createSignal, createEffect, on, onCleanup, For, Show } from 'solid-js';
import { A, useSearchParams } from '@solidjs/router';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useApi, isOk, type SeedCrackWorldDetailDto } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import { SEED_CRACK_STATUS_CRACKED, dimensionLabel } from '../services/seedCrack';
import styles from './SeedCrackerMap.module.css';

type Layer = { key: string; labelKey: string; color: string; observed?: boolean };

// Above this many 32-chunk regions in view, skip predicted-structure loading: only the near-full-world
// overview hits this, where thousands of markers are useless. Must match the backend cap. Markers render
// on a canvas layer so even a few thousand stay smooth.
const MAX_FEATURE_REGIONS = 16384;

// Marker layers (toggleable). Colours are map-marker hex (Leaflet needs literals, not CSS tokens).
const STRUCTURE_LAYERS: Layer[] = [
    { key: 'Village', labelKey: 'SEED_CRACK_MAP_VILLAGES', color: '#4caf50' },
    { key: 'DesertTemple', labelKey: 'SEED_CRACK_MAP_DESERT_TEMPLES', color: '#ffb300' },
    { key: 'JungleTemple', labelKey: 'SEED_CRACK_MAP_JUNGLE_TEMPLES', color: '#7cb342' },
    { key: 'WitchHut', labelKey: 'SEED_CRACK_MAP_WITCH_HUTS', color: '#ab47bc' },
];

// Compact biome colour key (subset), matching BiomeColors.cs on the backend.
const BIOME_KEY: { labelKey: string; color: string }[] = [
    { labelKey: 'SEED_CRACK_BIOME_OCEAN', color: '#000070' }, { labelKey: 'SEED_CRACK_BIOME_PLAINS', color: '#8db360' },
    { labelKey: 'SEED_CRACK_BIOME_FOREST', color: '#056621' }, { labelKey: 'SEED_CRACK_BIOME_DESERT', color: '#fa9418' },
    { labelKey: 'SEED_CRACK_BIOME_JUNGLE', color: '#537b09' }, { labelKey: 'SEED_CRACK_BIOME_TAIGA', color: '#0b6659' },
    { labelKey: 'SEED_CRACK_BIOME_SWAMP', color: '#34634f' }, { labelKey: 'SEED_CRACK_BIOME_SAVANNA', color: '#bdb25f' },
    { labelKey: 'SEED_CRACK_BIOME_ICE_SNOW', color: '#ffffff' }, { labelKey: 'SEED_CRACK_BIOME_MESA', color: '#d94515' },
    { labelKey: 'SEED_CRACK_BIOME_MUSHROOM', color: '#ff00ff' }, { labelKey: 'SEED_CRACK_BIOME_RIVER', color: '#0000ff' },
    { labelKey: 'SEED_CRACK_BIOME_EXTREME_HILLS', color: '#606060' }, { labelKey: 'SEED_CRACK_BIOME_BEACH', color: '#fade55' },
];

export default function SeedCrackerMap() {
    const api = useApi();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const worldKey = () => (searchParams.key as string | undefined) ?? '';

    const HIDDEN_KEY = 'seedmap.hiddenLayers';
    const loadHidden = (): Record<string, boolean> => {
        try { return JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '{}') ?? {}; } catch { return {}; }
    };

    const [phase, setPhase] = createSignal<'loading' | 'notfound' | 'uncracked' | 'ready'>('loading');
    const [detail, setDetail] = createSignal<SeedCrackWorldDetailDto | null>(null);
    const [cursor, setCursor] = createSignal<{ x: number; z: number } | null>(null);
    const [hidden, setHidden] = createSignal<Record<string, boolean>>(loadHidden());
    const [legendOpen, setLegendOpen] = createSignal(window.innerWidth > 768);

    let mapEl: HTMLDivElement | undefined;
    let map: L.Map | undefined;
    const groups: Record<string, L.LayerGroup> = {};
    let fetchTimer: number | undefined;
    let lastBoundsKey = '';

    const toggleLayer = (key: string) => {
        const next = { ...hidden(), [key]: !hidden()[key] };
        setHidden(next);
        try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(next)); } catch { /* ignore quota */ }
        const g = groups[key];
        if (!g || !map) return;
        if (next[key]) map.removeLayer(g); else g.addTo(map);
    };

    const loadFeatures = async () => {
        if (!map) return;
        const b = map.getBounds();
        // CRS.Simple: lat = MC Z, lng = MC X. north = max Z, south = min Z (don't swap these).
        const minChunkX = Math.floor(b.getWest() / 16) - 1;
        const maxChunkX = Math.ceil(b.getEast() / 16) + 1;
        const minChunkZ = Math.floor(b.getSouth() / 16) - 1;
        const maxChunkZ = Math.ceil(b.getNorth() / 16) + 1;
        const boundsKey = `${minChunkX},${minChunkZ},${maxChunkX},${maxChunkZ}`;
        if (boundsKey === lastBoundsKey) return;
        lastBoundsKey = boundsKey;

        // Zoomed-out enough that the viewport spans a huge region grid: thousands of markers help nobody and
        // the server won't compute it anyway. Drop the structure layers and skip the request.
        const regionSpan = (Math.floor(maxChunkX / 32) - Math.floor(minChunkX / 32) + 1)
            * (Math.floor(maxChunkZ / 32) - Math.floor(minChunkZ / 32) + 1);
        if (regionSpan > MAX_FEATURE_REGIONS) {
            for (const layer of STRUCTURE_LAYERS) groups[layer.key]?.clearLayers();
            return;
        }

        const res = await api.seedCrack.getMapFeatures({ key: worldKey(), minChunkX, minChunkZ, maxChunkX, maxChunkZ });
        if (!isOk(res.data) || !res.data.value || !map) return;
        const f = res.data.value;

        for (const layer of STRUCTURE_LAYERS) groups[layer.key]?.clearLayers();

        const addMarker = (layerKey: string, blockX: number, blockZ: number, title: string, color: string) => {
            const g = groups[layerKey];
            if (!g) return;
            // White ring + solid fill so the marker reads on any biome colour underneath.
            L.circleMarker([blockZ, blockX], {
                radius: 5,
                color: '#ffffff',
                weight: 2,
                opacity: 0.95,
                fillColor: color,
                fillOpacity: 1,
            }).bindTooltip(`${title} (${blockX}, ${blockZ})`).addTo(g);
        };

        for (const s of f.predicted ?? []) {
            const layer = STRUCTURE_LAYERS.find(l => l.key === s.kind);
            if (layer) addMarker(layer.key, s.blockX, s.blockZ, t(layer.labelKey), layer.color);
        }
    };

    const initMap = (w: SeedCrackWorldDetailDto) => {
        if (!mapEl) return;

        // World rectangle (lat = MC Z, lng = MC X). Tiles + panning are clamped to this so Leaflet never
        // requests the infinite plane outside the border (which rendered garbage tiles and starved the
        // render gate). maxNativeZoom 0 = 1px/block; minZoom fits the whole world in view, derived from
        // diameter so huge GG worlds don't try to stream millions of tiles.
        const half = w.diameter / 2;
        const worldBounds = L.latLngBounds(
            [w.centerZ - half, w.centerX - half],
            [w.centerZ + half, w.centerX + half],
        );
        const fitZoom = Math.min(0, Math.floor(Math.log2(640 / Math.max(w.diameter, 256))));
        const minZoom = fitZoom;
        // Ceiling is tied to native res (z=0, 1px/block) not fitZoom, so huge worlds still reach LOD detail.
        const maxZoom = Math.max(fitZoom + 9, 2);

        map = L.map(mapEl, {
            crs: L.CRS.Simple,
            minZoom,
            maxZoom,
            preferCanvas: true,
            zoomControl: true,
            attributionControl: false,
        });

        L.tileLayer(`/api/SeedCrack/map/tile/{z}/{x}/{y}?key=${encodeURIComponent(worldKey())}`, {
            tileSize: 256,
            minZoom,
            maxNativeZoom: 0,
            maxZoom,
            noWrap: true,
            bounds: worldBounds,
            updateWhenIdle: true,
            updateWhenZooming: false,
            keepBuffer: 1,
        }).addTo(map);

        L.rectangle(worldBounds, { color: '#888', weight: 1, fill: false, dashArray: '6 6' }).addTo(map);

        for (const layer of STRUCTURE_LAYERS) {
            groups[layer.key] = L.layerGroup();
            if (!hidden()[layer.key]) groups[layer.key].addTo(map);
        }

        map.fitBounds(worldBounds);
        map.on('mousemove', (e: L.LeafletMouseEvent) => setCursor({ x: Math.floor(e.latlng.lng), z: Math.floor(e.latlng.lat) }));
        map.on('click', (e: L.LeafletMouseEvent) => {
            const text = `${Math.floor(e.latlng.lng)} ${Math.floor(e.latlng.lat)}`;
            void navigator.clipboard?.writeText(text);
        });
        map.on('moveend', () => {
            window.clearTimeout(fetchTimer);
            fetchTimer = window.setTimeout(() => void loadFeatures(), 350);
        });
        void loadFeatures();
    };

    // Reactive on the key so navigating straight from one world's map to another reinitialises instead of
    // showing the previous world (same fix as the detail page).
    const setup = async (key: string) => {
        window.clearTimeout(fetchTimer);
        map?.remove();
        map = undefined;
        lastBoundsKey = '';
        setCursor(null);
        if (!key) { setPhase('notfound'); return; }
        setPhase('loading');
        const res = await api.seedCrack.getWorld({ key });
        if (key !== worldKey()) return; // key changed again mid-fetch
        if (!isOk(res.data) || !res.data.value) { setPhase('notfound'); return; }
        const w = res.data.value;
        setDetail(w);
        if (w.status !== SEED_CRACK_STATUS_CRACKED) { setPhase('uncracked'); return; }
        setPhase('ready');
        queueMicrotask(() => initMap(w));
    };

    createEffect(on(worldKey, (key) => { void setup(key); }));

    onCleanup(() => {
        window.clearTimeout(fetchTimer);
        map?.remove();
        map = undefined;
    });

    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <A href={`/seed-cracker/world?key=${encodeURIComponent(worldKey())}`} class={styles.backLink}>
                    <i class="fa-solid fa-arrow-left" /><Text val="SEED_CRACK_MAP_BACK" />
                </A>
                <Show when={detail()}>
                    {(w) => (
                        <div class={styles.titleRow}>
                            <i class="fa-solid fa-map" />
                            <h1>{w().citybuild || t('SEED_CRACK_CB_UNKNOWN')}</h1>
                            <span class={styles.dimBadge}><i class="fa-solid fa-earth-europe" />{dimensionLabel(t, w().dimension)}</span>
                        </div>
                    )}
                </Show>
            </div>

            <div class={styles.mapArea}>
                <div ref={mapEl} class={styles.map} />

                <Show when={phase() !== 'ready'}>
                    <div class={styles.overlay}>
                        <Show when={phase() === 'loading'}><i class="fa-solid fa-spinner fa-spin fa-2x" /></Show>
                        <Show when={phase() === 'notfound'}>
                            <i class="fa-solid fa-circle-question fa-2x" /><p><Text val="SEED_CRACK_DETAIL_NOT_FOUND" /></p>
                        </Show>
                        <Show when={phase() === 'uncracked'}>
                            <i class="fa-solid fa-lock fa-2x" /><p><Text val="SEED_CRACK_MAP_UNCRACKED" /></p>
                        </Show>
                    </div>
                </Show>

                <Show when={phase() === 'ready'}>
                    <button
                        type="button"
                        class={`btn btn-icon ${styles.legendToggle}`}
                        aria-expanded={legendOpen()}
                        title={t(legendOpen() ? 'SEED_CRACK_MAP_LEGEND_HIDE' : 'SEED_CRACK_MAP_LEGEND_SHOW')}
                        onClick={() => setLegendOpen((v) => !v)}
                    >
                        <i class={`fa-solid ${legendOpen() ? 'fa-xmark' : 'fa-layer-group'}`} />
                    </button>
                </Show>

                <Show when={phase() === 'ready' && legendOpen()}>
                    <div class={`fcard ${styles.legend}`}>
                        <div class={styles.legendSection}>
                            <span class={styles.legendTitle}><Text val="SEED_CRACK_MAP_STRUCTURES" /></span>
                            <For each={STRUCTURE_LAYERS}>{(layer) => (
                                <label class={styles.legendRow}>
                                    <input type="checkbox" checked={!hidden()[layer.key]} onChange={() => toggleLayer(layer.key)} />
                                    <span class={styles.swatch} style={{ 'background-color': layer.color, 'border-radius': layer.observed ? '50%' : '2px' }} />
                                    <Text val={layer.labelKey} />
                                </label>
                            )}</For>
                        </div>
                        <div class={styles.legendSection}>
                            <span class={styles.legendTitle}><Text val="SEED_CRACK_MAP_BIOMES" /></span>
                            <div class={styles.biomeGrid}>
                                <For each={BIOME_KEY}>{(b) => (
                                    <span class={styles.biomeItem}><span class={styles.swatch} style={{ 'background-color': b.color }} />{t(b.labelKey)}</span>
                                )}</For>
                            </div>
                        </div>
                        <div class={styles.coords}>
                            <Show when={cursor()} fallback={<Text val="SEED_CRACK_MAP_HOVER" />}>
                                {(c) => <span><i class="fa-solid fa-location-crosshairs" /> X {c().x}, Z {c().z}</span>}
                            </Show>
                        </div>
                    </div>
                </Show>
            </div>
        </div>
    );
}
